import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Truck, Settings, Package, MapPin, Loader2, Check, X, Plus, Trash2, ShieldBan, RotateCcw, KeyRound, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function CODSettings() {
  const [settings, setSettings] = useState({
    cod_enabled: false, cod_fee: 0, cod_min_order: 0, cod_areas: [], cod_require_otp: false, cod_blacklist: []
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newArea, setNewArea] = useState('');

  // Blacklist
  const [blEmail, setBlEmail] = useState('');
  const [blReason, setBlReason] = useState('');
  const [addingBl, setAddingBl] = useState(false);

  // OTP
  const [otpDialog, setOtpDialog] = useState(null); // order object
  const [otpCode, setOtpCode] = useState('');
  const [otpPreview, setOtpPreview] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  // Retur
  const [returDialog, setReturDialog] = useState(null);
  const [returReason, setReturReason] = useState('');
  const [returAction, setReturAction] = useState('return_to_seller');
  const [returProcessing, setReturProcessing] = useState(false);

  const fetchData = async () => {
    try {
      const [settingsRes, ordersRes] = await Promise.all([
        api.get('/tenant/cod-settings'),
        api.get('/tenant/cod-orders')
      ]);
      setSettings(settingsRes.data);
      setOrders(ordersRes.data.orders || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/cod-settings', {
        cod_enabled: settings.cod_enabled,
        cod_fee: settings.cod_fee,
        cod_min_order: settings.cod_min_order,
        cod_areas: settings.cod_areas,
        cod_require_otp: settings.cod_require_otp,
        cod_blacklist: settings.cod_blacklist || []
      });
      toast.success('Pengaturan COD disimpan');
    } catch (e) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  // --- COD Confirm (direct, no OTP) ---
  const handleConfirmCOD = async (orderId) => {
    try {
      await api.put(`/tenant/cod-orders/${orderId}/confirm`);
      fetchData();
      toast.success('COD dikonfirmasi');
    } catch (e) {
      toast.error('Gagal konfirmasi COD');
    }
  };

  // --- OTP ---
  const handleSendOTP = async (order) => {
    setOtpSending(true);
    try {
      const res = await api.post(`/tenant/cod-orders/${order.id}/send-otp`);
      setOtpDialog(order);
      setOtpPreview(res.data.otp_preview || '');
      setOtpCode('');
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal kirim OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode.trim()) return toast.error('Masukkan kode OTP');
    setOtpVerifying(true);
    try {
      await api.post(`/tenant/cod-orders/${otpDialog.id}/verify-otp?otp=${otpCode.trim()}`);
      toast.success('OTP valid! COD dikonfirmasi.');
      setOtpDialog(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'OTP salah');
    } finally {
      setOtpVerifying(false);
    }
  };

  // --- Retur ---
  const handleRetur = async () => {
    setReturProcessing(true);
    try {
      const res = await api.put(`/tenant/cod-orders/${returDialog.id}/retur`, {
        reason: returReason,
        action: returAction
      });
      toast.success(res.data.message);
      setReturDialog(null);
      setReturReason('');
      setReturAction('return_to_seller');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal proses retur');
    } finally {
      setReturProcessing(false);
    }
  };

  // --- Blacklist ---
  const handleAddBlacklist = async () => {
    if (!blEmail.trim()) return toast.error('Masukkan email customer');
    setAddingBl(true);
    try {
      await api.post('/tenant/cod-blacklist', { customer_email: blEmail.trim(), reason: blReason });
      toast.success('Customer ditambahkan ke blacklist');
      setBlEmail('');
      setBlReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    } finally {
      setAddingBl(false);
    }
  };

  const handleRemoveBlacklist = async (email) => {
    try {
      await api.delete(`/tenant/cod-blacklist/${encodeURIComponent(email)}`);
      toast.success('Blacklist dihapus');
      fetchData();
    } catch (e) {
      toast.error('Gagal hapus blacklist');
    }
  };

  // --- Area ---
  const addArea = () => {
    if (!newArea.trim()) return;
    if (settings.cod_areas.includes(newArea.trim())) { toast.error('Area sudah ada'); return; }
    setSettings({ ...settings, cod_areas: [...settings.cod_areas, newArea.trim()] });
    setNewArea('');
  };
  const removeArea = (area) => {
    setSettings({ ...settings, cod_areas: settings.cod_areas.filter(a => a !== area) });
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  const blacklist = (settings.cod_blacklist || []).map(b => typeof b === 'string' ? { email: b, reason: '', added_at: '' } : b);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div data-testid="cod-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>COD (Cash on Delivery)</h1>
        <p className="text-sm text-slate-500">Kelola pengaturan, pesanan, blacklist, dan retur COD</p>
      </div>

      <Tabs defaultValue="settings">
        <TabsList className="mb-6">
          <TabsTrigger value="settings" className="gap-2"><Settings size={14} /> Pengaturan</TabsTrigger>
          <TabsTrigger value="orders" className="gap-2"><Package size={14} /> Pesanan ({orders.length})</TabsTrigger>
          <TabsTrigger value="blacklist" className="gap-2"><ShieldBan size={14} /> Blacklist ({blacklist.length})</TabsTrigger>
        </TabsList>

        {/* ===== SETTINGS TAB ===== */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><Truck size={18} className="text-[#2D6CDF]" /> Pengaturan COD</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Aktifkan COD</p>
                    <p className="text-sm text-slate-500">Pelanggan bisa bayar saat barang tiba</p>
                  </div>
                  <Switch checked={settings.cod_enabled} onCheckedChange={(v) => setSettings({ ...settings, cod_enabled: v })} data-testid="cod-enabled-switch" />
                </div>
                <div>
                  <Label>Biaya COD (Rp)</Label>
                  <Input type="number" value={settings.cod_fee} onChange={(e) => setSettings({ ...settings, cod_fee: parseInt(e.target.value) || 0 })} placeholder="0" data-testid="cod-fee-input" />
                  <p className="text-xs text-slate-500 mt-1">Biaya tambahan untuk pembayaran COD</p>
                </div>
                <div>
                  <Label>Minimum Order untuk COD (Rp)</Label>
                  <Input type="number" value={settings.cod_min_order} onChange={(e) => setSettings({ ...settings, cod_min_order: parseInt(e.target.value) || 0 })} placeholder="0" data-testid="cod-min-order-input" />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Verifikasi OTP</p>
                    <p className="text-sm text-slate-500">Kirim OTP ke pelanggan saat kurir tiba</p>
                  </div>
                  <Switch checked={settings.cod_require_otp} onCheckedChange={(v) => setSettings({ ...settings, cod_require_otp: v })} />
                </div>
                <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-cod-settings-btn">
                  {saving && <Loader2 size={14} className="animate-spin mr-2" />} Simpan Pengaturan
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><MapPin size={18} className="text-emerald-500" /> Area COD</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">Daftar kota/kecamatan yang mendukung COD. Kosongkan untuk semua area.</p>
                <div className="flex gap-2">
                  <Input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="Nama kota/kecamatan..." onKeyPress={(e) => e.key === 'Enter' && addArea()} data-testid="new-area-input" />
                  <Button onClick={addArea} variant="outline" data-testid="add-area-btn"><Plus size={14} /></Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings.cod_areas.map((area, i) => (
                    <Badge key={i} variant="outline" className="pl-3 pr-1 py-1 flex items-center gap-1">
                      {area}
                      <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => removeArea(area)}><X size={12} /></Button>
                    </Badge>
                  ))}
                  {!settings.cod_areas.length && <p className="text-sm text-slate-400">Semua area didukung</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== ORDERS TAB (with OTP + Retur) ===== */}
        <TabsContent value="orders">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Order</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Customer</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">COD Status</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={5} className="py-12 text-center text-sm text-slate-400">Belum ada pesanan COD</td></tr>
                  )}
                  {orders.map(o => (
                    <tr key={o.id} className="border-b border-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{o.order_number}</p>
                        <p className="text-xs text-slate-500">{fmtDate(o.created_at)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-slate-900">{o.customer_name}</p>
                        <p className="text-xs text-slate-500">{o.customer_email}</p>
                        {o.customer_phone && <p className="text-xs text-slate-400">{o.customer_phone}</p>}
                      </td>
                      <td className="py-3 px-4 font-medium">Rp {fmt(o.total)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={
                          o.cod_status === 'confirmed' || o.payment_status === 'cod_confirmed' ? 'bg-emerald-100 text-emerald-700' :
                          o.cod_status === 'returned' || o.cod_status === 'failed' ? 'bg-red-100 text-red-700' :
                          o.cod_status === 'resending' ? 'bg-blue-100 text-blue-700' :
                          o.cod_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {o.cod_status || o.status}
                        </Badge>
                        {o.cod_otp_verified && <Badge className="ml-1 bg-emerald-100 text-emerald-700">OTP ✓</Badge>}
                        {o.cod_retur_reason && <p className="text-xs text-red-400 mt-1">Retur: {o.cod_retur_reason}</p>}
                      </td>
                      <td className="py-3 px-4 text-right space-x-1">
                        {/* Show confirm/OTP only for pending */}
                        {o.cod_status === 'pending' && o.payment_status !== 'cod_confirmed' && (
                          <>
                            {settings.cod_require_otp ? (
                              <Button size="sm" variant="outline" onClick={() => handleSendOTP(o)} disabled={otpSending}>
                                <KeyRound size={14} className="mr-1" /> OTP
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => handleConfirmCOD(o.id)} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`confirm-cod-${o.id}`}>
                                <Check size={14} className="mr-1" /> Konfirmasi
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-red-500 border-red-200" onClick={() => setReturDialog(o)}>
                              <RotateCcw size={14} className="mr-1" /> Retur
                            </Button>
                          </>
                        )}
                        {/* Resending can be confirmed again */}
                        {o.cod_status === 'resending' && (
                          <Button size="sm" onClick={() => handleConfirmCOD(o.id)} className="bg-emerald-500 hover:bg-emerald-600">
                            <Check size={14} className="mr-1" /> Konfirmasi
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== BLACKLIST TAB ===== */}
        <TabsContent value="blacklist">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><ShieldBan size={18} className="text-red-500" /> Blacklist COD</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">Customer yang di-blacklist tidak bisa menggunakan metode pembayaran COD. Customer otomatis di-blacklist setelah 2x gagal/retur COD.</p>
              <div className="flex gap-2">
                <Input value={blEmail} onChange={e => setBlEmail(e.target.value)} placeholder="Email customer" className="flex-1" />
                <Input value={blReason} onChange={e => setBlReason(e.target.value)} placeholder="Alasan (opsional)" className="flex-1" />
                <Button onClick={handleAddBlacklist} disabled={addingBl} className="bg-red-500 hover:bg-red-600">
                  {addingBl ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                </Button>
              </div>
              <div className="space-y-2">
                {blacklist.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Tidak ada customer yang di-blacklist</p>}
                {blacklist.map((b, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-red-50/50 border border-red-100 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{b.email}</p>
                      {b.reason && <p className="text-xs text-slate-500">{b.reason}</p>}
                      {b.added_at && <p className="text-xs text-slate-400">{fmtDate(b.added_at)}</p>}
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleRemoveBlacklist(b.email)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== OTP Dialog ===== */}
      <Dialog open={!!otpDialog} onOpenChange={(v) => !v && setOtpDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Verifikasi OTP COD</DialogTitle>
          </DialogHeader>
          {otpDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Order:</strong> {otpDialog.order_number}</p>
                <p><strong>Customer:</strong> {otpDialog.customer_name}</p>
                <p><strong>Phone:</strong> {otpDialog.customer_phone || '-'}</p>
                <p><strong>Total:</strong> Rp {fmt(otpDialog.total)}</p>
              </div>
              {otpPreview && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                  <p className="text-amber-700">OTP dikirim ke customer: <strong className="font-mono text-lg">{otpPreview}</strong></p>
                  <p className="text-xs text-amber-500 mt-1">Di production, OTP dikirim via WhatsApp/SMS</p>
                </div>
              )}
              <div>
                <Label>Masukkan kode OTP dari customer</Label>
                <Input value={otpCode} onChange={e => setOtpCode(e.target.value)} placeholder="6 digit kode OTP" maxLength={6} className="text-center text-2xl font-mono tracking-widest mt-1" />
              </div>
              <Button onClick={handleVerifyOTP} disabled={otpVerifying} className="w-full bg-emerald-500 hover:bg-emerald-600">
                {otpVerifying ? <Loader2 size={14} className="animate-spin mr-2" /> : <Check size={14} className="mr-2" />}
                Verifikasi & Konfirmasi COD
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Retur Dialog ===== */}
      <Dialog open={!!returDialog} onOpenChange={(v) => !v && setReturDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw size={18} /> Retur COD</DialogTitle>
          </DialogHeader>
          {returDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Order:</strong> {returDialog.order_number}</p>
                <p><strong>Customer:</strong> {returDialog.customer_name} ({returDialog.customer_email})</p>
                <p><strong>Total:</strong> Rp {fmt(returDialog.total)}</p>
              </div>
              <div>
                <Label>Alasan Retur</Label>
                <Textarea value={returReason} onChange={e => setReturReason(e.target.value)} placeholder="Customer menolak, alamat salah, dll..." rows={2} />
              </div>
              <div>
                <Label>Tindakan</Label>
                <Select value={returAction} onValueChange={setReturAction}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="return_to_seller">Retur ke Penjual</SelectItem>
                    <SelectItem value="resend">Kirim Ulang</SelectItem>
                    <SelectItem value="refund">Refund / Batalkan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-xs text-amber-700">
                ⚠ Customer yang gagal COD ≥2x akan otomatis di-blacklist dari COD.
              </div>
              <Button onClick={handleRetur} disabled={returProcessing} className="w-full bg-red-500 hover:bg-red-600">
                {returProcessing ? <Loader2 size={14} className="animate-spin mr-2" /> : <RotateCcw size={14} className="mr-2" />}
                Proses Retur
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
